/**
 * Scan Service
 * CD-286: My Scans list page
 * CD-287: Scan status view
 * CD-288: Scan initiation UI
 *
 * API service for scan operations via CDBE integration
 */
import api from './api'

export default {
  /**
   * List user's scan history
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {string} params.status - Filter by status
   */
  async listScans(params = {}) {
    // CD-460: Increased timeout because list_scans may sync released queue entries with CDBE
    return api.get('/scans', { params, timeout: 60000 })
  },

  /**
   * Get single scan by ID
   * @param {string} scanId - Scan ID
   */
  async getScan(scanId) {
    return api.get(`/scans/${scanId}`)
  },

  /**
   * Get scan status (for polling)
   * @param {string} scanId - Scan ID
   */
  async getScanStatus(scanId) {
    return api.get(`/scans/${scanId}/status`)
  },

  /**
   * Initiate a new scan
   * CD-460: Increased timeout to 120s because scan initiation calls CDBE
   * synchronously (preview + start), which can take over 30s for some domains.
   * @param {Object} data - Scan data
   * @param {string} data.domain - Domain to scan
   * @param {string} data.preset - Scan preset (quick/standard/deep/complete)
   * @param {Object} data.custom_config - Custom configuration
   */
  async initiateScan(data) {
    return api.post('/scans', data, { timeout: 120000 })
  },

  /**
   * Cancel a running scan
   * @param {string} scanId - Scan ID
   */
  async cancelScan(scanId) {
    return api.post(`/scans/${scanId}/cancel`)
  },

  /**
   * Get report download URL
   * @param {string} scanId - Scan ID
   * @param {string} format - Report format (html/pdf)
   */
  async getReport(scanId, format = 'html') {
    return api.get(`/scans/${scanId}/report`, { params: { format } })
  },

  /**
   * Get data export download URL
   * @param {string} scanId - Scan ID
   * @param {string} format - Export format (json/csv/xml)
   */
  async exportData(scanId, format = 'json') {
    return api.get(`/scans/${scanId}/export`, { params: { format } })
  },

  // Queue Management

  /**
   * Queue a new scan
   * @param {Object} data - Queue data
   * @param {string} data.domain_name - Domain to scan
   * @param {string} data.scan_type - Scan type (quick/standard/deep/complete)
   * @param {string} data.domain_id - Optional domain ID
   */
  async queueScan(data) {
    return api.post('/scans/queue', data)
  },

  /**
   * Get queue status (capacity info)
   */
  async getQueueStatus() {
    return api.get('/scans/queue/status')
  },

  /**
   * Get user's queued scans
   */
  async getMyQueuedScans() {
    return api.get('/scans/queue/my')
  },

  /**
   * Cancel a queued scan
   * @param {string} queueId - Queue item ID
   */
  async cancelQueuedScan(queueId) {
    return api.delete(`/scans/queue/${queueId}`)
  },

  /**
   * Get paginated domain results for a completed scan
   * @param {string} scanId - Scan or queue ID
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default 1)
   * @param {number} params.per_page - Results per page (default 50)
   * @param {string} params.sort_by - Sort field (default 'risk_score')
   * @param {string} params.order - Sort order (default 'desc')
   * @param {number} params.min_risk_score - Minimum risk score filter
   * @param {boolean} params.exists_only - Only resolved domains (default true)
   */
  async getDomainResults(scanId, params = {}) {
    return api.get(`/scans/${scanId}/results`, { params })
  },

  /**
   * Queue multiple scans at once
   * @param {Object} data - Batch scan data
   * @param {string[]} data.domains - List of domains to scan
   * @param {string} data.scan_type - Scan preset
   */
  async batchQueueScans(data) {
    return api.post('/scans/queue/batch', data)
  }
}
